package dev.lingvo.shared.screenshots.color

import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow

/** Uses linear sRGB luminance so caption checks match WCAG 2.x rather than perceptual lightness. */
fun contrastRatio(first: String, second: String): Double {
    fun luminance(hex: String): Double {
        require(Regex("^#[0-9A-Fa-f]{6}$").matches(hex)) { "Contrast requires opaque sRGB" }
        fun channel(start: Int): Double {
            val c = hex.substring(start, start + 2).toInt(16) / 255.0
            return if (c <= .04045) c / 12.92 else ((c + .055) / 1.055).pow(2.4)
        }
        return .2126 * channel(1) + .7152 * channel(3) + .0722 * channel(5)
    }
    val a = luminance(first)
    val b = luminance(second)
    return (max(a, b) + .05) / (min(a, b) + .05)
}

/** Walks ink lightness away from the field, then chooses the readable endpoint if that direction is exhausted. */
fun guardContrast(ink: String, backgrounds: List<String>, target: Double): String {
    require(backgrounds.isNotEmpty() && target in 1.0..21.0)
    fun readable(candidate: String): Boolean = backgrounds.all { contrastRatio(candidate, it) >= target }
    if (readable(ink)) return ink
    val lch = srgbHexToOkLab(ink).toLch()
    val direction = if (lch.l >= backgrounds.map { srgbHexToOkLab(it).l }.average()) 1 else -1
    var lightness = lch.l
    repeat(51) {
        lightness = (lightness + direction * .02).coerceIn(0.0, 1.0)
        val candidate = gamutMap(lch.copy(l = lightness))
        if (readable(candidate)) return candidate
    }
    val endpoints = listOf("#111111", "#FFFFFF", "#000000")
    return endpoints.firstOrNull(::readable)
        ?: throw IllegalArgumentException("No common ink meets contrast on these backgrounds")
}
